import React, {
  type Dispatch,
  type FC,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  type Play,
  type PlayEdit,
  type PlayTriggerType,
  type PlayType,
  playSlice,
  PlayPresentation,
} from '@bbkAdminRedux/rtkq/play.slice';
import {
  RoutingNodeEnum,
  RoutingPercentEnum,
  type TopPlayTargetingNode,
} from '@bbkAdminUtils/api-client/rest-routing-api';
import { produce } from 'immer';
import _, { type PropertyPath } from 'lodash';
import {
  useAppLocation,
  useHistory,
  useLocation,
  useParams,
} from '@bbkShared/react-router';
import { generateNewIdInt } from '@bbkAdminComponents/experiences/offers/utils';
import {
  type PercentNodeType,
  type PercentToTargetType,
  type RoutingNodeType,
} from '@bbkAdminUtils/api-client/routing.types';
import { OfferNodeType } from '@bbkAdminComponents/play/play-offer-node-select';
import {
  appPersistSlice,
  selectAppMisc,
} from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { useSelector } from 'react-redux';
import { type RuleMetadata } from '@bbkAdminRedux/rtkq/rules.slice';
import {
  OfferObjective,
  OfferPresentation,
  PublishState,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { useSelectAllOffers } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import { type SelectOption } from '@bbkAdminComponents/select/select';
import { TriggerEventType } from '@bbkAdminComponents/play/play-trigger';
import { type SingleOfferErrorType } from '@bbkAdminComponents/play/offer-components/single-offer';
import { type ABTestOfferErrorType } from '@bbkAdminComponents/play/offer-components/test-offer';
import { type SubAudOfferErrorType } from '@bbkAdminComponents/play/offer-components/sub-aud-offer';
import icons from '@bbkAdminUtils/icons';
import { useUrl } from '@bbkAdminUtils/nav';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import {
  FunnelType,
  useSelectBrandActiveFunnels,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import { useGetSelectedBrand } from '@bbkAdminRedux/rtkq/brands.slice';
import { type RiskLevel } from '../predictive/customers-preview';
import {
  CustomOptionRenderer,
  CustomValueRenderer,
} from '../experiences/audiences/edit-rule/condition-item';
import {
  type GroupBase,
  type OptionProps,
  type SingleValueProps,
} from 'react-select';
import { BBKRequestContext } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import {
  CtaProcessingOption,
  RestModalAction,
  type RestModalRequires,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { createGlobalState } from 'react-use';
import { selectApp, selectCompany } from '@bbkAdminRedux/app/selectors';
import { useGetProductLicense } from '@bbkAdminUtils/plans';
import { selectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { useDuplicatePlay } from '@bbkAdminComponents/unified-plays/utils/use-duplicate-play';

export const useDisableDemoReports = createGlobalState<boolean>(false);

export const useShowDemoData = ({
  disableDemoReports = false,
}: { disableDemoReports?: boolean } = {}) => {
  const demoDataEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_DEMO_REPORTS);
  const app = useSelector(selectApp);

  return (app?.show_demo_data || demoDataEnabled) && !disableDemoReports;
};

export const useShowPlayDemoData = ({
  disableDemoReports = false,
}: { disableDemoReports?: boolean } = {}) => {
  const app = useSelector(selectCurrentApp);
  const demoDataEnabled = useFeatureFlags(FeatureFlagsE.ENABLE_DEMO_REPORTS);
  const isNewPlayPage = useIsNewPlayPage();

  if (isNewPlayPage) {
    return false;
  }

  return (app?.show_play_demo_data || demoDataEnabled) && !disableDemoReports;
};

const NEW_PLAY_PATH_PATTERN = new RegExp(`${AuthorizedPaths.playNew}(/|$)`);
const UNIFIED_NEW_PLAY_PATH_PATTERN = new RegExp(
  `${AuthorizedPaths.unifiedPlaysNew}(/|$)`
);
export const useIsNewPlayPage = () => {
  const location = useLocation();
  return (
    NEW_PLAY_PATH_PATTERN.test(location.pathname) ||
    UNIFIED_NEW_PLAY_PATH_PATTERN.test(location.pathname)
  );
};

export const ADD_OFFER_VALUE = 'ADD_OFFER_VALUE';
export enum PlayTargetType {
  OFFER = 'OFFER',
  HOSTED_PAGE = 'HOSTED_PAGE',
  NONE = 'NONE',
  PRICING_PAGE = 'PRICING_PAGE',
  CANCEL_PAGE = 'CANCEL_PAGE',
}

export const offerPresentationTargetMapping: Record<
  PlayPresentation,
  PlayTargetType
> = {
  [PlayPresentation.MODAL]: PlayTargetType.OFFER,
  [PlayPresentation.POP_UP]: PlayTargetType.OFFER,
  [PlayPresentation.BANNER]: PlayTargetType.OFFER,
  [PlayPresentation.CARD]: PlayTargetType.HOSTED_PAGE,
  [PlayPresentation.PRICING_PAGE]: PlayTargetType.PRICING_PAGE,
  [PlayPresentation.CANCEL_PAGE]: PlayTargetType.CANCEL_PAGE,
};

export const triggerTargetMapping: Record<
  TriggerEventType,
  OfferPresentation[]
> = {
  [TriggerEventType.ANY_PAGE_VISIT]: [
    OfferPresentation.modal,
    OfferPresentation.banner,
    OfferPresentation.pop_up,
  ],
  [TriggerEventType.ON_CLICK]: [OfferPresentation.card],
  [TriggerEventType.PAGE_VISIT]: [
    OfferPresentation.modal,
    OfferPresentation.banner,
    OfferPresentation.pop_up,
  ],
  [TriggerEventType.OTHER]: [],
};

const initialPlay: PlayEdit = {
  name: 'New Play',
  play_type: 'UPSELL',
  rule_id: '',
  draft_targeting: {
    type: RoutingNodeEnum.percentRoutingNode,
    name: 'top_level',
    routing_percents: [
      {
        type: RoutingPercentEnum.routingToTargetPercent,
        name: 'control',
        percent: 0,
        target: {
          type: PlayTargetType.NONE,
          target_key: 'none',
        },
      },
    ],
  } as TopPlayTargetingNode,
};

export enum PlayObjectProperty {
  AUDIENCE_ID = 'audience_id',
  CONTROL_PERCENTAGE = 'draft_control_percentage',
  CONTROL_TARGET = 'draft_control_target',
  OFFER_PERCENTAGE = 'draft_offer_percentage',
  LIVE_CONTROL_PERCENTAGE = 'live_control_percentage',
  LIVE_CONTROL_TARGET = 'live_control_target',
  LIVE_OFFER_PERCENTAGE = 'live_offer_percentage',
  PLAY_TRIGGER = 'trigger_rules',
  PLAY_TITLE = 'play_title',
  PLAY_TYPE = 'play_type',
  PRESENTATION = 'presentation',
  LIVE_PRESENTATION = 'live_presentation',
  OFFER_NODE_TYPE = 'offer_node_type',
  CONTROL_NODE = 'draft_control_node',
  OFFER_NODE = 'draft_offer_node',
  LIVE_CONTROL_NODE = 'live_control_node',
  LIVE_OFFER_NODE = 'live_offer_node',
  BRAND_ID = 'brand_id',
  PRIORITY = 'priority',
}

const draftLiveSelectorMapping: Partial<
  Record<PlayObjectProperty, PlayObjectProperty>
> = {
  [PlayObjectProperty.CONTROL_PERCENTAGE]:
    PlayObjectProperty.LIVE_CONTROL_PERCENTAGE,
  [PlayObjectProperty.CONTROL_TARGET]: PlayObjectProperty.LIVE_CONTROL_TARGET,
  [PlayObjectProperty.OFFER_PERCENTAGE]:
    PlayObjectProperty.LIVE_OFFER_PERCENTAGE,
  [PlayObjectProperty.CONTROL_NODE]: PlayObjectProperty.LIVE_CONTROL_NODE,
  [PlayObjectProperty.OFFER_NODE]: PlayObjectProperty.LIVE_OFFER_NODE,
  [PlayObjectProperty.PRESENTATION]: PlayObjectProperty.LIVE_PRESENTATION,
};

type PlayFieldResultTypes = {
  [PlayObjectProperty.AUDIENCE_ID]: string;
  [PlayObjectProperty.CONTROL_PERCENTAGE]: number;
  [PlayObjectProperty.CONTROL_TARGET]: string;
  [PlayObjectProperty.OFFER_PERCENTAGE]: number;
  [PlayObjectProperty.LIVE_CONTROL_PERCENTAGE]: number;
  [PlayObjectProperty.LIVE_CONTROL_TARGET]: string;
  [PlayObjectProperty.LIVE_OFFER_PERCENTAGE]: number;
  [PlayObjectProperty.PLAY_TRIGGER]: PlayTriggerType;
  [PlayObjectProperty.PLAY_TITLE]: string;
  [PlayObjectProperty.PLAY_TYPE]: PlayType;
  [PlayObjectProperty.PRESENTATION]: PlayPresentation;
  [PlayObjectProperty.LIVE_PRESENTATION]: PlayPresentation;
  [PlayObjectProperty.OFFER_NODE_TYPE]: OfferNodeType;
  [PlayObjectProperty.CONTROL_NODE]: PercentToTargetType;
  [PlayObjectProperty.OFFER_NODE]: RoutingNodeType | PercentToTargetType;
  [PlayObjectProperty.LIVE_CONTROL_NODE]: PercentToTargetType;
  [PlayObjectProperty.LIVE_OFFER_NODE]: RoutingNodeType | PercentToTargetType;
  [PlayObjectProperty.BRAND_ID]?: string;
  [PlayObjectProperty.PRIORITY]?: number;
};

export const PlayObjectPropertyPath = {
  [PlayObjectProperty.AUDIENCE_ID]: 'rule_id',
  [PlayObjectProperty.CONTROL_PERCENTAGE]:
    'draft_targeting.routing_percents[0].percent',
  [PlayObjectProperty.CONTROL_TARGET]:
    'draft_targeting.routing_percents[0].target.target_key',
  [PlayObjectProperty.OFFER_PERCENTAGE]:
    'draft_targeting.routing_percents[1].percent',
  [PlayObjectProperty.LIVE_CONTROL_PERCENTAGE]:
    'targeting.routing_percents[0].percent',
  [PlayObjectProperty.LIVE_CONTROL_TARGET]:
    'targeting.routing_percents[0].target.target_key',
  [PlayObjectProperty.LIVE_OFFER_PERCENTAGE]:
    'targeting.routing_percents[1].percent',
  [PlayObjectProperty.PLAY_TRIGGER]: 'trigger_rules',
  [PlayObjectProperty.PLAY_TITLE]: 'name',
  [PlayObjectProperty.PRESENTATION]: 'presentation',
  [PlayObjectProperty.LIVE_PRESENTATION]: 'presentation',
  [PlayObjectProperty.OFFER_NODE_TYPE]: 'offer_node_type',
  [PlayObjectProperty.CONTROL_NODE]: 'draft_targeting.routing_percents[0]',
  [PlayObjectProperty.OFFER_NODE]: 'draft_targeting.routing_percents[1]',
  [PlayObjectProperty.LIVE_CONTROL_NODE]: 'targeting.routing_percents[0]',
  [PlayObjectProperty.LIVE_OFFER_NODE]: 'targeting.routing_percents[1]',
  [PlayObjectProperty.PLAY_TYPE]: 'play_type',
  [PlayObjectProperty.BRAND_ID]: 'brand_id',
  [PlayObjectProperty.PRIORITY]: 'priority',
};

export enum PlayValidationErrorType {
  Trigger = 'trigger',
  Audience = 'audience',
  Offer = 'offer',
}
type GenericSelectPredictiveAudiencesOpts =
  | {
      components?: {
        Option: FC<OptionProps<unknown, boolean, GroupBase<unknown>>>;
        SingleValue: FC<SingleValueProps<unknown, boolean, GroupBase<unknown>>>;
      };
    }
  | undefined;
type PlayValidationError = {
  [PlayValidationErrorType.Audience]?: {
    type: PlayValidationErrorType;
    error: string;
    show: boolean;
  };
  [PlayValidationErrorType.Trigger]?: {
    type: PlayValidationErrorType;
    error: {
      type: TriggerEventType;
      triggerErrors: Array<string | undefined>;
      show: boolean;
    };
  };
  [PlayValidationErrorType.Offer]?:
    | SingleOfferErrorType
    | ABTestOfferErrorType
    | SubAudOfferErrorType;
};

export const PlayContext = React.createContext<{
  play: PlayEdit;
  hasDraftChanged: boolean;
  activeEditor: PublishState;
  setActiveEditor: (state: PublishState) => void;
  resetControlSelection: () => void;
  resetPresentationSelection: () => void;
  resetOfferSelection: (type: OfferNodeType) => void;
  resetOfferNode: () => void;
  getPlayObjectProperty: <T extends PlayObjectProperty>(
    field: T
  ) => PlayFieldResultTypes[T] | undefined;
  setPlayObjectProperty: <T extends PlayObjectProperty>(
    field: T,
    value: PlayFieldResultTypes[T] | undefined
  ) => void;
  getActiveTarget: () => PlayTargetType;
  getDropdownTitle: () => string;
  isPresentationAllowedForTriggerType: (
    offerPresentation: PlayPresentation
  ) => boolean;
  offerOptions: SelectOption<string>[];
  showManageOfferWarning: (offerId?: string) => boolean;
  playValidationErrors: PlayValidationError;
  setPlayValidationErrors: Dispatch<SetStateAction<PlayValidationError>>;
  initializeTargetingNode: (type: OfferNodeType) => void;
  handleCreateNewOffer: () => void;
  handleEditOffer: (field?: string | undefined, isCopy?: boolean) => void;
  handleEditActionItem: (itemId: string, currentStep?: number) => void;
  handleCrossObjectiveOfferSelect: (offer: RestOffer) => void;
  allOffers: RestOffer[];
  genericSelectPredictiveAudiencesOpts: GenericSelectPredictiveAudiencesOpts;
  priorityUpdates: Array<{ id: string; priority: number }>;
  setPriorityUpdates: Dispatch<
    SetStateAction<Array<{ id: string; priority: number }>>
  >;
  isDuplicatePlayLoading: boolean;
  duplicateFromPlayId: string | undefined;
}>(undefined!);

const PlayContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const appLocation = useAppLocation();
  const { appKey, playId } = useParams<{ appKey: string; playId: string }>();
  const company = useSelector(selectCompany);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const createUrl = useUrl();
  const hostedPageEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_LIFECYCLE_HOSTED_PAGES
  );
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const appMiscState = useSelector(selectAppMisc(appKey));

  const [disableDemoReports] = useDisableDemoReports();
  const showDemoData = useShowPlayDemoData({ disableDemoReports });

  const { data: billingConfig, isFetching: isFetchingBillingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );

  const isMultiBranded = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );
  const showPredictiveAudiences = useFeatureFlags(
    FeatureFlagsE.ENABLE_PREDICTIVE
  );
  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const growthV2License = productLicense.growthV2License;

  const [activeEditor, setActiveEditor] = useState<PublishState>(
    PublishState.draft
  );
  const brandId = useGetSelectedBrand(appKey);
  const [play, setPlay] = useState<PlayEdit>(initialPlay);
  const [playValidationErrors, setPlayValidationErrors] =
    useState<PlayValidationError>({});
  const [savedPlay, setSavedPlay] = useState<Play | undefined>();
  const [hasDraftChanged, setHasDraftChanged] = useState<boolean>(!playId);
  const [priorityUpdates, setPriorityUpdates] = useState<
    Array<{ id: string; priority: number }>
  >([]);

  const { data: playData } = playSlice.endpoints.getPlay.useQuery(
    playId && appKey
      ? {
          appKey,
          playId,
          context: showDemoData
            ? BBKRequestContext.lifecycle_reports
            : undefined,
        }
      : skipToken
  );

  const location = useLocation<{
    fromAudience?: boolean;
    fromOfferEditor?: boolean;
    fromPlayEditor?: boolean;
    fromPricingTable?: boolean;
    rule?: RuleMetadata;
    offerId?: string;
    pricingTableId?: string;
    riskCategory?: RiskLevel;
    isNewPlay?: boolean;
    offerPresentation?: PlayPresentation;
    presentationType?: PlayPresentation; // For unified plays editor
    playName?: string;
    duplicateFromPlayId?: string;
    playType?: PlayType;
    brandId?: string;
  }>();

  // Fetch the play to duplicate
  const duplicateFromPlayId = location.state?.duplicateFromPlayId;
  const { data: playToDuplicate, isLoading: isDuplicatePlayLoading } =
    playSlice.endpoints.getPlay.useQuery(
      duplicateFromPlayId && appKey && !playId
        ? {
            appKey,
            playId: duplicateFromPlayId,
            context: showDemoData
              ? BBKRequestContext.lifecycle_reports
              : undefined,
          }
        : skipToken
    );

  useEffect(() => {
    if (!playData) return;

    // Ensure draft_targeting exists with valid structure
    const playWithTargeting = produce(playData, (draft) => {
      if (!draft.draft_targeting) {
        // Initialize with default targeting structure if missing
        draft.draft_targeting = {
          type: RoutingNodeEnum.percentRoutingNode,
          name: 'top_level',
          routing_percents: [
            {
              type: RoutingPercentEnum.routingToTargetPercent,
              name: 'control',
              percent: 0,
              target: {
                type: PlayTargetType.NONE,
                target_key: 'none',
              },
            },
          ],
        } as TopPlayTargetingNode;
      }
    });

    setPlay(playWithTargeting);
    setSavedPlay(playWithTargeting);
  }, [playData]);

  const initialOffers = useSelectAllOffers({
    appKey,
    brandId: isMultiBranded ? brandId : undefined,
    context: showDemoData ? BBKRequestContext.lifecycle_reports : undefined,
  });

  const allOffers = initialOffers;

  useEffect(() => {
    const state = location.state;

    if (state?.fromAudience && appMiscState?.playState?.play) {
      // Combine play restoration and audience insertion in one setPlay call
      if (state?.rule) {
        const pathToSet = appMiscState.playState?.subAudiencePath
          ? appMiscState.playState.subAudiencePath
          : PlayObjectPropertyPath[PlayObjectProperty.AUDIENCE_ID];

        const updatedPlay = produce(appMiscState.playState.play, (draft) => {
          _.set(draft, pathToSet as PropertyPath, state.rule?.id);
        });
        setPlay(updatedPlay);
      } else {
        setPlay(appMiscState.playState.play);
      }

      // Restore the saved step when returning from audience editor
      if (appMiscState.playState.currentStep !== undefined) {
        // Update location state to include initialStep for the play editor
        history.replace(location.pathname + location.search, {
          ...state,
          initialStep: appMiscState.playState.currentStep,
        });
      }
    }
    if (
      (state?.fromOfferEditor || state?.fromPlayEditor) &&
      appMiscState?.playState?.play
    ) {
      // Combine both play restoration and offer insertion in one setPlay call
      if (appMiscState.playState.addNewOffer && state?.offerId) {
        const updatedPlay = produce(appMiscState.playState.play, (draft) => {
          const targetValue = {
            target_key: state?.offerId,
            type: getActiveTarget(),
          };
          _.set(
            draft,
            appMiscState.playState?.addNewOffer?.path as PropertyPath,
            targetValue
          );
        });
        setPlay(updatedPlay);
      } else {
        setPlay(appMiscState.playState.play);
      }

      // Restore the saved step when returning from offer editor
      if (appMiscState.playState.currentStep !== undefined) {
        // Update location state to include initialStep for the play editor
        history.replace(location.pathname + location.search, {
          ...state,
          initialStep: appMiscState.playState.currentStep,
        });
      }
    }
    if (state?.fromPricingTable && appMiscState?.playState?.play) {
      // Combine play restoration and pricing table insertion in one setPlay call
      if (appMiscState.playState.addNewOffer && state?.pricingTableId) {
        const updatedPlay = produce(appMiscState.playState.play, (draft) => {
          _.set(
            draft,
            appMiscState.playState?.addNewOffer?.path as PropertyPath,
            { target_key: state?.pricingTableId, type: getActiveTarget() }
          );
        });
        setPlay(updatedPlay);
      } else {
        setPlay(appMiscState.playState.play);
      }

      // Restore the saved step when returning from pricing table editor
      if (appMiscState.playState.currentStep !== undefined) {
        // Update location state to include initialStep for the play editor
        history.replace(location.pathname + location.search, {
          ...state,
          initialStep: appMiscState.playState.currentStep,
        });
      }
    }
    if (state?.isNewPlay && state?.offerPresentation) {
      setPlayObjectProperty(
        PlayObjectProperty.PRESENTATION,
        state?.offerPresentation as PlayPresentation
      );
    }

    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: undefined,
      })
    );
  }, []);

  const handleCreateNewOffer = () => {
    history.push({
      pathname: createUrl(AuthorizedPaths.offersNew),
      search: encodeObjectForQuery({ returnUrl: appLocation.appUrl }),
      state: {
        fromPlayEditor: true,
        aiFlow: !isMultiBranded,

        brand_id: getPlayObjectProperty(PlayObjectProperty.BRAND_ID),
        objective: getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE),
        presentation: getPlayObjectProperty(PlayObjectProperty.PRESENTATION),
      },
    });
  };
  const handleEditOffer = (offerId?: string, isCopy: boolean = false) => {
    let newOffer: RestOffer | undefined;
    if (isCopy) {
      const offer = offers.find((offer) => offer.id === offerId);
      if (offer) {
        newOffer = {
          ...offer,
          display_name: `${offer.display_name}-${offers.length + 1}`,
        };
      }
    }
    const editUrl = createUrl(AuthorizedPaths.offersEdit, {
      offerId: isCopy ? undefined : offerId,
    });

    history.push({
      pathname: editUrl,
      search: encodeObjectForQuery({ returnUrl: appLocation.appUrl }),
      state: {
        fromPlayEditor: true,
        aiFlow: !isMultiBranded,
        brand_id: getPlayObjectProperty(PlayObjectProperty.BRAND_ID),
        objective: getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE),
        presentation: getPlayObjectProperty(PlayObjectProperty.PRESENTATION),
        newOffer: newOffer,
      },
    });
  };

  const handleEditActionItem = (itemId: string, currentStep?: number) => {
    const presentation = getPlayObjectProperty(PlayObjectProperty.PRESENTATION);

    dispatch(
      appPersistSlice.actions.saveAppMisc({
        appKey,
        playState: {
          play,
          currentStep,
        },
      })
    );

    if (presentation === PlayPresentation.PRICING_PAGE) {
      const returnUrl =
        window.location.pathname +
        window.location.search +
        (window.location.hash || '');

      history.push({
        pathname: createUrl(AuthorizedPaths.pricingTableEditor, {
          pricingTableId: itemId,
        }),
        search: encodeObjectForQuery({ returnUrl }),
        state: {
          fromPricingTable: true,
        },
      });
    } else if (
      presentation === PlayPresentation.MODAL ||
      presentation === PlayPresentation.POP_UP ||
      presentation === PlayPresentation.BANNER
    ) {
      handleEditOffer(itemId);
    }
  };

  const getDefaultValues = (field: PlayObjectProperty) => {
    switch (field) {
      case PlayObjectProperty.AUDIENCE_ID:
      case PlayObjectProperty.PLAY_TRIGGER: {
        return undefined;
      }
      case PlayObjectProperty.CONTROL_PERCENTAGE: {
        return 0;
      }
      case PlayObjectProperty.PLAY_TITLE: {
        return '';
      }
      case PlayObjectProperty.PRESENTATION: {
        return undefined;
      }
      default: {
        return undefined;
      }
    }
  };

  const getPlayObjectProperty = <T extends PlayObjectProperty>(field: T) => {
    let propertyPath = PlayObjectPropertyPath[field];
    if (activeEditor == PublishState.live) {
      if (field === PlayObjectProperty.OFFER_NODE_TYPE) {
        return liveOfferNodeType as PlayFieldResultTypes[T] | undefined;
      }

      if (field in draftLiveSelectorMapping) {
        propertyPath =
          PlayObjectPropertyPath[draftLiveSelectorMapping[field]!!];
      }
    }

    if (field === PlayObjectProperty.OFFER_NODE_TYPE) {
      return offerNodeType as PlayFieldResultTypes[T] | undefined;
    }

    return _.get(play, propertyPath, getDefaultValues(field)) as
      | PlayFieldResultTypes[T]
      | undefined;
  };

  const offerPresentation = getPlayObjectProperty(
    PlayObjectProperty.PRESENTATION
  );
  const playType = getPlayObjectProperty(
    PlayObjectProperty.PLAY_TYPE
  ) as string;
  const currentPlayBrandId = getPlayObjectProperty(PlayObjectProperty.BRAND_ID);
  const hostedPageFunnels = useSelectBrandActiveFunnels(
    {
      appKey,
      funnelType: FunnelType.LIFECYCLE,
      brandId: currentPlayBrandId,
      context: BBKRequestContext.lifecycle_reports,
    },
    !hostedPageEnabled
  );

  const offers = useMemo(() => {
    if (!offerPresentation) return initialOffers;
    return initialOffers.filter(
      (o) =>
        o.active &&
        o.presentation === offerPresentation &&
        o.objective === playType
    );
  }, [initialOffers, offerPresentation, playType]);

  const handleCrossObjectiveOfferSelect = useCallback(
    (offer: RestOffer) => {
      let copiedOfferData = {
        ...offer,
        display_name: `${offer.display_name}`,
        objective: getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE),
        presentation: getPlayObjectProperty(PlayObjectProperty.PRESENTATION),
      };

      let crossObjectiveConversionApplied = false;

      // Fix invalid processing options when copying FROM a retention offer
      if (offer.objective === OfferObjective.retention) {
        copiedOfferData = produce(copiedOfferData, (draft) => {
          const primaryCard = draft.cards?.find(
            (card) => card.name === draft.primary_card_name
          );

          if (primaryCard) {
            // If 3 cards, delete last 2 (keep only primary card)
            if (draft.cards && draft.cards.length === 3) {
              draft.cards = [primaryCard];
              crossObjectiveConversionApplied = true;
            }

            const forwards = (primaryCard.requires as any)?.forwards;

            if (forwards) {
              // Convert modal_email and modal_email_structured to link
              if (
                forwards.action === RestModalAction.modal_email ||
                forwards.action === RestModalAction.modal_email_structured
              ) {
                forwards.action = RestModalAction.link;
                crossObjectiveConversionApplied = true;

                delete forwards.modal;
                if (!forwards.url) {
                  forwards.url = `https://${company?.domain}`;
                }
              }

              // Convert webhook/email processing options to URL
              if (forwards.cta_details_list) {
                forwards.cta_details_list.forEach((ctaDetails: any) => {
                  if (
                    ctaDetails.processing_option ===
                      CtaProcessingOption.email ||
                    ctaDetails.processing_option === CtaProcessingOption.webhook
                  ) {
                    ctaDetails.processing_option = CtaProcessingOption.url;
                    crossObjectiveConversionApplied = true;
                  }
                });
              }
            }

            draft.cards?.forEach((card) => {
              if ((card as any).cards) {
                delete (card as any).cards;
              }
            });
          }
        });
      } else if (offer.objective === OfferObjective.acquisition) {
        copiedOfferData = produce(copiedOfferData, (draft) => {
          draft.cards?.forEach((card) => {
            const forwards = (card.requires as any)?.forwards;
            if (forwards?.cta_details_list) {
              forwards.cta_details_list.forEach((ctaDetails: any) => {
                if (
                  ctaDetails.processing_option ===
                  CtaProcessingOption.chargebee_checkout
                ) {
                  ctaDetails.processing_option = CtaProcessingOption.url;
                  delete ctaDetails.checkout_eligibility_rule;
                  delete ctaDetails.checkout_layout;
                  crossObjectiveConversionApplied = true;
                }
              });
            }
          });
        });
      }

      const editUrl = createUrl(AuthorizedPaths.offersEdit, {
        offerId: undefined,
      });

      history.push({
        pathname: editUrl,
        search: encodeObjectForQuery({ returnUrl: appLocation.appUrl }),
        state: {
          fromPlayEditor: true,
          aiFlow: !isMultiBranded,
          brand_id: getPlayObjectProperty(PlayObjectProperty.BRAND_ID),
          objective: getPlayObjectProperty(PlayObjectProperty.PLAY_TYPE),
          presentation: getPlayObjectProperty(PlayObjectProperty.PRESENTATION),
          newOffer: copiedOfferData,
          notifyUser: crossObjectiveConversionApplied,
        },
      });
    },
    [
      allOffers,
      getPlayObjectProperty,
      createUrl,
      history,
      appLocation,
      isMultiBranded,
    ]
  );

  const getActiveTarget = (): PlayTargetType => {
    const presentation = getPlayObjectProperty(PlayObjectProperty.PRESENTATION);

    if (presentation && hostedPageEnabled) {
      return offerPresentationTargetMapping[presentation];
    }

    return PlayTargetType.OFFER;
  };

  const getDropdownTitle = (): string => {
    switch (getActiveTarget()) {
      case PlayTargetType.HOSTED_PAGE:
        return 'Choose offer page';
      case PlayTargetType.OFFER:
      default:
        return 'Choose offer';
    }
  };

  const offerOptions: SelectOption<string>[] = useMemo(() => {
    switch (getActiveTarget()) {
      case PlayTargetType.HOSTED_PAGE:
        return hostedPageFunnels.map((funnel) => ({
          label: funnel.display_name ?? 'Funnel',
          value: funnel.encoded_id,
        }));
      case PlayTargetType.OFFER:
        const offerOptions = offers.map((offer) => ({
          label: offer.display_name,
          value: offer.id,
        }));
        return [
          {
            label: (
              <p className="tw-font-semibold tw-text-cbrprimary-500">
                + New offer {isMultiBranded ? '' : 'with Retention AI '}
                {isMultiBranded ? '' : icons.aiStar}
              </p>
            ),
            value: ADD_OFFER_VALUE,
          },
          ...offerOptions,
        ];

      default:
        return [];
    }
  }, [hostedPageFunnels, offers, isMultiBranded]);

  const resetOfferNode = () => {
    setOfferNodeType(undefined);
    const updatedPlay = produce(play, (draft) => {
      const routingPercents = draft.draft_targeting?.routing_percents as Array<
        PercentNodeType | PercentToTargetType
      >;
      _.pullAt(routingPercents, 1);
      if (!playId) {
        _.set(draft, PlayObjectProperty.PRESENTATION, play.presentation);
      }
    });
    setPlay(updatedPlay);
    resetControlSelection();
  };

  const initializeTargetingNode = (type: OfferNodeType) => {
    if (!type) return;

    // automatically changes node type if passed type does not match current type (Ex: Offer -> AB test change)
    if (type !== offerNodeType) {
      setOfferNodeType(type);
    }

    const controlPercent = getPlayObjectProperty(
      PlayObjectProperty.CONTROL_PERCENTAGE
    ) as number;
    switch (type) {
      case OfferNodeType.OFFER: {
        const node = {
          type: RoutingPercentEnum.routingToTargetPercent,
          name: generateNewIdInt('pct_target'),
          percent: 100 - controlPercent,
          target: {
            type: getActiveTarget(),
            target_key: '',
          },
        };
        setPlay((prevPlay) => {
          return produce(prevPlay, (draft) => {
            _.set(
              draft,
              PlayObjectPropertyPath[PlayObjectProperty.OFFER_NODE],
              node
            );
          });
        });
        break;
      }
      case OfferNodeType.TEST: {
        const node = {
          type: RoutingPercentEnum.routingToNodePercent,
          name: 'all_other_offers',
          percent: 100 - controlPercent,
          routing_node: {
            type: RoutingNodeEnum.percentRoutingNode,
          },
        };
        setPlay((prevPlay) => {
          return produce(prevPlay, (draft) => {
            _.set(
              draft,
              PlayObjectPropertyPath[PlayObjectProperty.OFFER_NODE],
              node
            );
          });
        });
        break;
      }
      case OfferNodeType.SUB_AUDI_SPLIT: {
        const node = {
          type: RoutingPercentEnum.routingToNodePercent,
          name: 'all_other_offers',
          percent: 100 - controlPercent,
          routing_node: {
            type: RoutingNodeEnum.rulesRoutingNode,
          },
        };
        setPlay((prevPlay) => {
          return produce(prevPlay, (draft) => {
            _.set(
              draft,
              PlayObjectPropertyPath[PlayObjectProperty.OFFER_NODE],
              node
            );
          });
        });
        break;
      }
    }
  };

  const setPlayObjectProperty = <T extends PlayObjectProperty>(
    field: T,
    value: PlayFieldResultTypes[T] | undefined
  ) => {
    if (field === PlayObjectProperty.OFFER_NODE_TYPE) {
      if (value === undefined) return;
      initializeTargetingNode(value as OfferNodeType);
      return;
    }
    setPlay((prevPlay) => {
      return produce(prevPlay, (draft) => {
        _.set(draft, PlayObjectPropertyPath[field], value);
      });
    });
    // Reinitialize targeting node whenever presentation mode changes, init done after setPlay to get updated presentation
    if (field === PlayObjectProperty.PRESENTATION && offerNodeType) {
      initializeTargetingNode(offerNodeType);
    }
  };

  useDuplicatePlay({
    playToDuplicate,
    duplicateFromPlayId,
    playName: location.state?.playName,
    playType: location.state?.playType,
    presentation:
      location.state?.offerPresentation || location.state?.presentationType,
    brandId: location.state?.brandId,
    setPlayObjectProperty,
    onDuplicationComplete: () => {
      setHasDraftChanged(true);
    },
  });

  const resetControlSelection = () => {
    const controlNode = getPlayObjectProperty(
      PlayObjectProperty.CONTROL_NODE
    ) as PercentToTargetType;
    const updatedNode = produce(controlNode, (draft) => {
      _.set(draft, ['target'], {
        type: PlayTargetType.NONE,
        target_key: 'none',
      });
    });
    setPlayObjectProperty(PlayObjectProperty.CONTROL_NODE, updatedNode);
    setPlayObjectProperty(PlayObjectProperty.CONTROL_PERCENTAGE, 0);
    if (getPlayObjectProperty(PlayObjectProperty.OFFER_PERCENTAGE)) {
      setPlayObjectProperty(PlayObjectProperty.OFFER_PERCENTAGE, 100);
    }
  };

  const resetPresentationSelection = () => {
    setPlayObjectProperty(PlayObjectProperty.PRESENTATION, undefined);
  };

  const resetOfferSelection = (type: OfferNodeType) => {
    resetControlSelection();
    switch (type) {
      case OfferNodeType.OFFER: {
        setPlay((prevPlay) => {
          return produce(prevPlay, (draft) => {
            const path =
              PlayObjectPropertyPath[PlayObjectProperty.OFFER_NODE] + '.target';
            _.omit(draft, path);
          });
        });
        break;
      }
      case OfferNodeType.TEST: {
        setPlay((prevPlay) => {
          return produce(prevPlay, (draft) => {
            const path =
              PlayObjectPropertyPath[PlayObjectProperty.OFFER_NODE] +
              '.routing_node.routing_percents';
            _.omit(draft, path);
          });
        });
        break;
      }
      case OfferNodeType.SUB_AUDI_SPLIT: {
        setPlay((prevPlay) => {
          return produce(prevPlay, (draft) => {
            const path =
              PlayObjectPropertyPath[PlayObjectProperty.OFFER_NODE] +
              '.routing_node.routing_rules';
            _.omit(draft, path);
          });
        });
        break;
      }
    }
  };

  const showManageOfferWarning = useCallback(
    (offerId?: string) => {
      if (!offerId || billingConfig?.manage_offers) return false;
      const offer = initialOffers.find((offer) => offer.id === offerId);
      if (offer) {
        const processingOptions =
          offer.cards
            .flatMap(
              (card) =>
                (card.requires as RestModalRequires)?.forwards
                  ?.cta_details_list || []
            )
            .map((cta) => cta.processing_option) ?? [];
        const processingOptionsExtra =
          offer.cards
            .flatMap(
              (card) => (card.requires as RestModalRequires)?.other_actions
            )
            .flatMap((otherAction) => otherAction?.cta_details_list || [])
            .map((cta) => cta.processing_option) ?? [];
        const hasBillingProcessing = [
          ...processingOptions,
          ...processingOptionsExtra,
        ].some((op) => op === CtaProcessingOption.billing);
        if (!hasBillingProcessing) return false;
        if (!billingConfig?.manage_offers) {
          return true;
        }
      }
      return false;
    },
    [initialOffers]
  );

  const [offerNodeType, setOfferNodeType] = useState<OfferNodeType | undefined>(
    undefined
  );

  const [liveOfferNodeType, setLiveOfferNodeType] = useState<
    OfferNodeType | undefined
  >(undefined);

  const selectOfferNodeType = <T extends PlayObjectProperty>(
    field: T,
    setOfferNode: (offerNodeType: OfferNodeType) => void
  ) => {
    const offerNode = getPlayObjectProperty(field);
    if (offerNode === undefined) return;
    if (
      _.get(offerNode, 'type', undefined) ===
      RoutingPercentEnum.routingToTargetPercent
    ) {
      setOfferNode(OfferNodeType.OFFER);
    }
    if (
      _.get(offerNode, 'routing_node.type', undefined) ===
      RoutingNodeEnum.percentRoutingNode
    ) {
      setOfferNode(OfferNodeType.TEST);
    }
    if (
      _.get(offerNode, 'routing_node.type', undefined) ===
      RoutingNodeEnum.rulesRoutingNode
    ) {
      setOfferNode(OfferNodeType.SUB_AUDI_SPLIT);
    }
  };

  const areNodesEqual = (
    obj1: TopPlayTargetingNode | undefined,
    obj2: TopPlayTargetingNode | undefined,
    skipFields = ['name']
  ) => {
    if (obj1 === undefined || obj2 === undefined) return true;
    const structureCompare = (value1: any, value2: any): boolean => {
      if (Array.isArray(value1) && Array.isArray(value2)) {
        if (value1.length !== value2.length) return false;
        return value1.every((item, index) =>
          structureCompare(item, value2[index])
        );
      }

      if (
        _.isObject(value1) &&
        _.isObject(value2) &&
        !Array.isArray(value1) &&
        !Array.isArray(value2)
      ) {
        const allKeys1 = Object.keys(value1 as object);
        const allKeys2 = Object.keys(value2 as object);

        const keys1 = allKeys1.filter((k) => !skipFields.includes(k));
        const keys2 = allKeys2.filter((k) => !skipFields.includes(k));

        const hasSameKeys =
          keys1.every((k) => allKeys2.includes(k)) &&
          keys2.every((k) => allKeys1.includes(k));

        if (!hasSameKeys) return false;

        return keys1.every((key) => {
          const val1 = (value1 as Record<string, any>)[key];
          const val2 = (value2 as Record<string, any>)[key];

          if (_.isObject(val1) && _.isObject(val2)) {
            return structureCompare(val1, val2);
          }

          return val1 === val2;
        });
      }

      return value1 === value2;
    };

    return structureCompare(obj1, obj2);
  };

  useEffect(() => {
    selectOfferNodeType(PlayObjectProperty.OFFER_NODE, (offerNodeType) => {
      setOfferNodeType(offerNodeType);
    });
    selectOfferNodeType(PlayObjectProperty.LIVE_OFFER_NODE, (offerNodeType) => {
      setLiveOfferNodeType(offerNodeType);
    });

    const isPlayDetailsChanged =
      !_.isEqual(
        _.pick(play, ['play_trigger', 'trigger_rules', 'rule_id']),
        _.pick(savedPlay, ['play_trigger', 'trigger_rules', 'rule_id'])
      ) || !areNodesEqual(play.draft_targeting, savedPlay?.draft_targeting);

    setHasDraftChanged(isPlayDetailsChanged);
  }, [play]);

  const isPresentationAllowedForTriggerType = (
    offerPresentation: PlayPresentation
  ): boolean => {
    const playTriggerEventType = getPlayObjectProperty(
      PlayObjectProperty.PLAY_TRIGGER
    )?.event_type;

    return playTriggerEventType
      ? triggerTargetMapping[playTriggerEventType].includes(
          offerPresentation as any
        )
      : true;
  };
  const shouldShowPredictiveAudienceOptions =
    showPredictiveAudiences ||
    ((agsLicense || growthV2License) && showDemoData);
  const genericSelectPredictiveAudiencesOpts =
    shouldShowPredictiveAudienceOptions
      ? {
          components: {
            Option: CustomOptionRenderer,
            SingleValue: CustomValueRenderer,
          },
        }
      : undefined;
  return (
    <PlayContext.Provider
      value={{
        play,
        activeEditor,
        hasDraftChanged,
        setActiveEditor,
        resetControlSelection,
        resetOfferSelection,
        resetPresentationSelection,
        resetOfferNode,
        getPlayObjectProperty,
        setPlayObjectProperty,
        getActiveTarget,
        getDropdownTitle,
        offerOptions,
        showManageOfferWarning,
        playValidationErrors,
        setPlayValidationErrors,
        handleCreateNewOffer,
        initializeTargetingNode,
        isPresentationAllowedForTriggerType,
        handleEditOffer,
        handleEditActionItem,
        handleCrossObjectiveOfferSelect,
        allOffers,
        genericSelectPredictiveAudiencesOpts,
        priorityUpdates,
        setPriorityUpdates,
        isDuplicatePlayLoading,
        duplicateFromPlayId,
      }}
    >
      {children}
    </PlayContext.Provider>
  );
};

export default PlayContextProvider;
